import React, { useRef, useState, useCallback, memo, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import EmojiPicker, { EmojiStyle } from "emoji-picker-react";
import { Plus, Smile, AtSign, Hash, SendHorizontal, X, Image, Film, File as FileIcon, Mic, AlertCircle, Pencil } from 'lucide-react';
import {PhoneHQSpinner, PhoneHQAvatar, PhoneHQPopover, PhoneHQDropdown} from 'ch3-ui-lib';
import MentionTextInput, { MentionTextInputRef, PlainTableInsertedInfo } from './MentionTextInput';
import ComposerFormattingToolbar from './ComposerFormattingToolbar';
import FilePreview from './FilePreview';
import VoiceRecordingPreview from './VoiceRecordingPreview';
import { useVoiceRecording } from '../../hooks/useVoiceRecording';
import { hexToEmoji } from "../Emoji/emoji";
import type { RoomMentionTarget } from "./MentionTextInput";
import { matchesMentionQuery } from '../../utils/mentionUtils';
import { checkStatusExpiration } from "../../utilis/statusUtilis.ts";
import { logChatError } from '../../utils/loggerHelpers';
import { useIdResolver } from "../../hooks/useIdResolver";
import {
    BOLD_MARKER,
    CODE_MARKER,
    ITALIC_MARKER,
    STRIKETHROUGH_MARKER,
    wrapSelection,
    type FormattingMarker,
} from '../../utils/composerFormatting';
import {
    closeCodeBlock,
    reduceCodeBlockChange,
    type CodeBlockState,
} from '../../utils/codeBlockComposerState';
import { hasSendableContent, buildTextMessageContent, buildReplyMessageContent } from '../../utils/messageFormatting';
import { buildEditContent } from '../../utils/messageEdits';
import { composePrivateReplyMessage } from '../../utils/privateReplyQuote';
import { MATRIX_MAX_EVENT_BYTES, estimateSendSizeBytes, bytesToKB } from '../../utils/messageSizeLimit';

interface ChatMessageInputUser {
    matrixId: string;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
}

interface ChatMessageInputProps {
    selectedRoomId: string | null;
    roomUsers: { userId: string; displayName: string; avatarUrl?: string }[];
    usersToCreateChat: ChatMessageInputUser[];
    currentRoomName?: string;
    replyToMessage: string | null;
    onReplyCancel: () => void;
    privateReplyTarget: { senderName: string; excerpt: string } | null;
    onPrivateReplyCancel: () => void;
    availableRooms?: RoomMentionTarget[];
    editingMessage: string | null;
    editingBody: string | null;
    editingMentions?: { userId: string; displayName: string }[];
    editingRoomMentions?: RoomMentionTarget[];
    onEditCancel: () => void;
    onSendMessage: (message: string, mentions: { userId: string; displayName: string }[], files: File[], roomMentions?: RoomMentionTarget[]) => Promise<void>;
    isRoomEncrypted?: boolean;
    maxAttachments?: number;
    onAttachmentLimitExceeded?: (maxAttachments: number) => void;
    onTyping: () => void;
    getReplyMessagePreview: () => React.ReactNode;
    disabled?: boolean;
    inputHeight: number;
    onHeightChange: (height: number) => void;
    diffrentTimeZone?: boolean;
    onDismissTimezone?: () => void;
    timezoneMessage?: React.ReactNode;
}

interface RoomDraft {
    message: string;
    mentions: { userId: string; displayName: string }[];
    roomMentions: RoomMentionTarget[];
    files: File[];
    filePreviews: {file: File; preview?: string; type: string}[];
    audioURL: string | null;
    audioBlob: Blob | null;
    audioLevels: number[];
}

interface AttachmentLimitNotice {
    maxAttachments: number;
    acceptedCount: number;
    rejectedCount: number;
    rejectedFileNames: string[];
}

const parkedComposerText = (text: string, block: CodeBlockState): string => {
    if (!block) return text;
    if (block.language === '' && text.slice(block.start).trim() === '') {
        return text.slice(0, block.start);
    }
    return closeCodeBlock(text, block).text;
};

const isStillPastedTable = (nextText: string, info: PlainTableInsertedInfo): boolean => {
    if (nextText === info.prefix + info.suffix) return false;
    if (!nextText.startsWith(info.prefix)) return false;
    const afterPrefix = nextText.slice(info.prefix.length);
    if (afterPrefix === '') return false;
    if (info.insertedText.startsWith(afterPrefix)) return true;
    if (afterPrefix.startsWith(info.insertedText)) {
        return afterPrefix.slice(info.insertedText.length) === info.suffix;
    }
    return false;
};

const ChatMessageInput: React.FC<ChatMessageInputProps> = memo(({
    selectedRoomId,
    roomUsers,
    usersToCreateChat,
    availableRooms,
    currentRoomName,
    replyToMessage,
    onReplyCancel,
    privateReplyTarget,
    onPrivateReplyCancel,
    editingMessage,
    editingBody,
    editingMentions = [],
    editingRoomMentions = [],
    onEditCancel,
    onSendMessage,
    isRoomEncrypted = false,
    maxAttachments = Number.POSITIVE_INFINITY,
    onAttachmentLimitExceeded,
    onTyping,
    getReplyMessagePreview,
    disabled = false,
    inputHeight,
    onHeightChange,
    diffrentTimeZone,
    timezoneMessage

}) => {
    const intl = useIntl();
    const [newMessage, setNewMessage] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [currentMentions, setCurrentMentions] = useState<{ userId: string; displayName: string }[]>([]);
    const [currentRoomMentions, setCurrentRoomMentions] = useState<RoomMentionTarget[]>([]);
    const [showRoomSuggestions, setShowRoomSuggestions] = useState(false);
    const [filteredRooms, setFilteredRooms] = useState<RoomMentionTarget[]>([]);
    const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
    const [filteredUsers, setFilteredUsers] = useState<any>([]);
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [filePreviews, setFilePreviews] = useState<{file: File; preview?: string; type: string}[]>([]);
    const [isSending, setIsSending] = useState(false);
    const [inputWidth, setInputWidth] = useState<number | undefined>(undefined);
    const [attachmentLimitNotice, setAttachmentLimitNotice] = useState<AttachmentLimitNotice | null>(null);
    const [editingUiSuppressed, setEditingUiSuppressed] = useState(false);
    const isEditingNow = !!editingMessage && !editingUiSuppressed;
    const [codeBlockState, setCodeBlockState] = useState<CodeBlockState>(null);
    const [formatSelection, setFormatSelection] = useState<{ start: number; end: number } | null>(null);
    const [pasteTableUndo, setPasteTableUndo] = useState<PlainTableInsertedInfo | null>(null);
    const [tooLargeError, setTooLargeError] = useState<{ estimatedBytes: number } | null>(null);
    const codeBlockStateRef = useRef<CodeBlockState>(null);
    const previousComposerTextRef = useRef('');
    const pasteTableUndoRef = useRef<PlainTableInsertedInfo | null>(null);

    useEffect(() => {
        const el = document.getElementById('mention-text-input');
        if (!el) return;
        const observer = new ResizeObserver(([entry]) => {
            setInputWidth(entry.contentRect.width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const [currentRoomAudioURL, setCurrentRoomAudioURL] = useState<string | null>(null);
    const [currentRoomAudioBlob, setCurrentRoomAudioBlob] = useState<Blob | null>(null);
    const [currentRoomAudioLevels, setCurrentRoomAudioLevels] = useState<number[]>([]);

    const roomDraftsRef = useRef<Map<string, RoomDraft>>(new Map());
    const previousRoomIdRef = useRef<string | null>(null);
    const draftBeforeEditRef = useRef<RoomDraft | null>(null);
    const latestDraftRef = useRef<RoomDraft>({
        message: '',
        mentions: [],
        roomMentions: [],
        files: [],
        filePreviews: [],
        audioURL: null,
        audioBlob: null,
        audioLevels: [],
    });

    const {
        isRecording,
        recordingLength,
        audioURL,
        audioBlob,
        startRecording,
        stopRecording,
        cancelRecording,
        clearRecordingWithoutRevoke,
        hasRecording,
        audioLevels,
    } = useVoiceRecording();

    const recordingControlsRef = useRef({isRecording, cancelRecording, clearRecordingWithoutRevoke});
    recordingControlsRef.current = {isRecording, cancelRecording, clearRecordingWithoutRevoke};

    const effectiveAudioURL = currentRoomAudioURL || audioURL;
    const effectiveAudioBlob = currentRoomAudioBlob || audioBlob;
    const effectiveAudioLevels = currentRoomAudioLevels.length > 0 ? currentRoomAudioLevels : audioLevels;
    const effectiveHasRecording = isEditingNow ? false : (!!currentRoomAudioBlob || hasRecording);
    const currentAttachmentCount = selectedFiles.length + (effectiveHasRecording ? 1 : 0);
    const availableAttachmentSlots = Math.max(0, maxAttachments - currentAttachmentCount);
    const isAttachmentLimitReached = availableAttachmentSlots <= 0;

    const mentionInputRef = useRef<MentionTextInputRef | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const videoInputRef = useRef<HTMLInputElement>(null);
    const documentInputRef = useRef<HTMLInputElement>(null);
    const inputRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);
    const mentionListRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!replyToMessage || disabled) return;

        const animationFrame = requestAnimationFrame(() => {
            mentionInputRef.current?.focus();
        });

        return () => cancelAnimationFrame(animationFrame);
    }, [replyToMessage, disabled]);

    const handleComposerTextChange = useCallback((nextText: string, forceApply = false) => {
        setTooLargeError(null);
        if (pasteTableUndoRef.current && !isStillPastedTable(nextText, pasteTableUndoRef.current)) {
            pasteTableUndoRef.current = null;
            setPasteTableUndo(null);
        }

        const result = reduceCodeBlockChange(previousComposerTextRef.current, codeBlockStateRef.current, nextText);
        previousComposerTextRef.current = result.text;
        codeBlockStateRef.current = result.block;
        setNewMessage(result.text);
        setCodeBlockState(result.block);
        if (result.rewritten || forceApply) {
            mentionInputRef.current?.applyTextChange(result.text, result.selection.start);
        }
    }, []);

    const handlePlainTableInserted = useCallback((info: PlainTableInsertedInfo) => {
        pasteTableUndoRef.current = info;
        setPasteTableUndo(info);
    }, []);

    const handleUndoPasteTable = useCallback(() => {
        if (!pasteTableUndo) return;

        const {prefix, suffix, originalText} = pasteTableUndo;
        const restored = prefix + originalText + suffix;
        previousComposerTextRef.current = restored;
        pasteTableUndoRef.current = null;
        setNewMessage(restored);
        setPasteTableUndo(null);
        mentionInputRef.current?.applyTextChange(restored, prefix.length + originalText.length);
    }, [pasteTableUndo]);

    const applyFormatting = useCallback((marker: FormattingMarker) => {
        const offsets = mentionInputRef.current?.getSelectionOffsets();
        if (!offsets || offsets.start === offsets.end) return;

        const wrapped = wrapSelection(newMessage, offsets.start, offsets.end, marker);
        if (wrapped.text === newMessage) return;

        const folded = reduceCodeBlockChange(newMessage, codeBlockStateRef.current, wrapped.text);
        previousComposerTextRef.current = folded.text;
        codeBlockStateRef.current = folded.block;
        setCodeBlockState(folded.block);
        setFormatSelection(null);

        const caret = folded.rewritten ? folded.selection.start : wrapped.selection.start;
        mentionInputRef.current?.applyTextChange(folded.text, caret);
    }, [newMessage]);

    const handleExitCodeBlock = useCallback(() => {
        if (!codeBlockState) return;

        const closed = closeCodeBlock(newMessage, codeBlockState);
        previousComposerTextRef.current = closed.text;
        codeBlockStateRef.current = null;
        setCodeBlockState(null);
        setNewMessage(closed.text);
        mentionInputRef.current?.applyTextChange(closed.text, closed.selection.start);
    }, [codeBlockState, newMessage]);

    const allUsers = useMemo(() => {
        return roomUsers.length ? roomUsers : usersToCreateChat;
    }, [roomUsers, usersToCreateChat]);

    const { getUserId, allStatuses, isError: isStatusError } = useIdResolver();

    const usersWithStatus = useMemo(() => {
        try {
            return allUsers.map(user => {
                const matrixId = 'userId' in user ? user.userId : 'matrixId' in user ? user.matrixId : null;
                if (!matrixId) return user;

                const resolvedUserId = getUserId(matrixId);
                const status = !isStatusError && resolvedUserId
                    ? allStatuses.find(s => String(s.userId) === String(resolvedUserId))
                    : undefined;

                return {
                    ...user,
                    ...status,
                    userId: matrixId
                };
            });
        } catch (error) {
            logChatError('Error merging user statuses', undefined, 'mergeUserStatuses');
            return allUsers;
        }
    }, [allUsers, allStatuses, isStatusError, getUserId]);

    const isCustomStatusExpired = useCallback((user: any) => {
        if (!user.customUserStatus?.dueDate) return true;

        let isExpired = true;
        checkStatusExpiration(user, (expired: boolean) => {
            isExpired = expired;
        });
        return isExpired;
    }, []);

    const getStatusDisplayInfo = useCallback((user: any) => {
        const hasCustomStatus = user.customUserStatus?.name && !isCustomStatusExpired(user);
        const hasCustomIcon = user.customUserStatus?.icon && !isCustomStatusExpired(user);

        if (hasCustomStatus) {
            return {
                hasStatus: true,
                statusName: user.customUserStatus.name,
                statusIcon: hasCustomIcon ? user.customUserStatus.icon : null
            };
        }

        return {
            hasStatus: false,
            statusName: null,
            statusIcon: null
        };
    }, [isCustomStatusExpired]);

    const clearAllFiles = useCallback(() => {
        filePreviews.forEach(item => {
            if (item.preview) {
                URL.revokeObjectURL(item.preview);
            }
        });
        setSelectedFiles([]);
        setFilePreviews([]);
        setAttachmentLimitNotice(null);
    }, [filePreviews]);

    const showAttachmentLimitNotice = useCallback((acceptedCount: number, rejectedFiles: File[] = []) => {
        setAttachmentLimitNotice({
            maxAttachments,
            acceptedCount,
            rejectedCount: rejectedFiles.length,
            rejectedFileNames: rejectedFiles.map(file => file.name),
        });
        onAttachmentLimitExceeded?.(maxAttachments);
    }, [maxAttachments, onAttachmentLimitExceeded]);

    const addFiles = useCallback((files: File[]) => {
        if (files.length === 0) return;

        if (availableAttachmentSlots <= 0) {
            showAttachmentLimitNotice(0, files);
            return;
        }

        const filesToAdd = files.slice(0, availableAttachmentSlots);
        const rejectedFiles = files.slice(availableAttachmentSlots);
        if (rejectedFiles.length > 0) {
            showAttachmentLimitNotice(filesToAdd.length, rejectedFiles);
        } else {
            setAttachmentLimitNotice(null);
        }

        const newFilePreviews = filesToAdd.map(file => {
            const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
            return {
                file,
                preview,
                type: file.type
            };
        });

        setSelectedFiles(prev => [...prev, ...filesToAdd]);
        setFilePreviews(prev => [...prev, ...newFilePreviews]);
    }, [availableAttachmentSlots, showAttachmentLimitNotice]);

    latestDraftRef.current = {
        message: parkedComposerText(newMessage, codeBlockState),
        mentions: currentMentions,
        roomMentions: currentRoomMentions,
        files: selectedFiles,
        filePreviews,
        audioURL: currentRoomAudioURL,
        audioBlob: currentRoomAudioBlob,
        audioLevels: currentRoomAudioLevels,
    };

    useEffect(() => {
        setEditingUiSuppressed(false);
        pasteTableUndoRef.current = null;
        setPasteTableUndo(null);
        setTooLargeError(null);

        if (editingMessage) {
            if (draftBeforeEditRef.current === null) {
                draftBeforeEditRef.current = latestDraftRef.current;
                setSelectedFiles([]);
                setFilePreviews([]);
                setCurrentRoomAudioURL(null);
                setCurrentRoomAudioBlob(null);
                setCurrentRoomAudioLevels([]);
                if (recordingControlsRef.current.isRecording) {
                    recordingControlsRef.current.cancelRecording();
                } else {
                    recordingControlsRef.current.clearRecordingWithoutRevoke();
                }
            }
            previousComposerTextRef.current = editingBody ?? '';
            codeBlockStateRef.current = null;
            setCodeBlockState(null);
            setFormatSelection(null);
            setNewMessage(editingBody ?? '');
            setCurrentMentions(editingMentions);
            setCurrentRoomMentions(editingRoomMentions);
            setTimeout(() => mentionInputRef.current?.focus(), 0);
        } else if (draftBeforeEditRef.current !== null) {
            const restored = draftBeforeEditRef.current;
            draftBeforeEditRef.current = null;
            previousComposerTextRef.current = restored.message;
            codeBlockStateRef.current = null;
            setCodeBlockState(null);
            setFormatSelection(null);
            setNewMessage(restored.message);
            setCurrentMentions(restored.mentions);
            setCurrentRoomMentions(restored.roomMentions);
            setSelectedFiles(restored.files);
            setFilePreviews(restored.filePreviews);
            setCurrentRoomAudioURL(restored.audioURL);
            setCurrentRoomAudioBlob(restored.audioBlob);
            setCurrentRoomAudioLevels(restored.audioLevels);
        }
    }, [editingMessage, editingBody, editingMentions, editingRoomMentions]);

    const handleSend = useCallback(async () => {
        const effectiveMessage = codeBlockState ? closeCodeBlock(newMessage, codeBlockState).text : newMessage;
        if ((!hasSendableContent(effectiveMessage, currentMentions, currentRoomMentions) && selectedFiles.length === 0 && !effectiveHasRecording) || isSending) return;

        const trimmedMessage = effectiveMessage.trim();
        if (trimmedMessage) {
            let contentToCheck: unknown;
            if (isEditingNow && editingMessage) {
                contentToCheck = buildEditContent(editingMessage, trimmedMessage, currentMentions, currentRoomMentions, editingMentions.map(mention => mention.userId));
            } else if (privateReplyTarget) {
                const composed = composePrivateReplyMessage(trimmedMessage, privateReplyTarget, currentMentions, currentRoomMentions);
                contentToCheck = {
                    msgtype: 'm.text',
                    body: composed.body,
                    ...(composed.formattedBody !== undefined ? {format: composed.format, formatted_body: composed.formattedBody} : {}),
                    ...(currentMentions.length > 0 ? {'m.mentions': {user_ids: currentMentions.map(mention => mention.userId)}} : {}),
                };
            } else if (replyToMessage) {
                contentToCheck = buildReplyMessageContent(trimmedMessage, replyToMessage, currentMentions, currentRoomMentions);
            } else {
                contentToCheck = buildTextMessageContent(trimmedMessage, currentMentions, currentRoomMentions);
            }
            const estimatedBytes = estimateSendSizeBytes(contentToCheck, isRoomEncrypted);
            if (estimatedBytes > MATRIX_MAX_EVENT_BYTES) {
                setTooLargeError({estimatedBytes});
                return;
            }
        }
        setTooLargeError(null);

        const wasEditing = Boolean(editingMessage);

        setIsSending(true);
        try {
            const filesToSend = [...selectedFiles];
            if (effectiveHasRecording && effectiveAudioBlob) {
                const voiceFile = new File([effectiveAudioBlob], `voice-message-${Date.now()}.wav`, { type: 'audio/wav' });
                filesToSend.push(voiceFile);
            }

            if (filesToSend.length > maxAttachments) {
                showAttachmentLimitNotice(maxAttachments, filesToSend.slice(maxAttachments));
                return;
            }

            await onSendMessage(effectiveMessage.trim(), currentMentions, filesToSend, currentRoomMentions);

            codeBlockStateRef.current = null;
            setCodeBlockState(null);
            setFormatSelection(null);
            pasteTableUndoRef.current = null;
            setPasteTableUndo(null);

            if (wasEditing) {
                setTimeout(() => {
                    mentionInputRef.current?.focus();
                }, 0);
                return;
            }

            const currentRoomKey = selectedRoomId || 'new-chat';
            roomDraftsRef.current.delete(currentRoomKey);

            previousComposerTextRef.current = '';
            setNewMessage('');
            setCurrentMentions([]);
            setCurrentRoomMentions([]);
            clearAllFiles();
            if (currentRoomAudioURL) {
                URL.revokeObjectURL(currentRoomAudioURL);
            }
            setCurrentRoomAudioURL(null);
            setCurrentRoomAudioBlob(null);
            setCurrentRoomAudioLevels([]);
            setAttachmentLimitNotice(null);
            cancelRecording();

            setTimeout(() => {
                mentionInputRef.current?.focus();
            }, 0);
        } catch (error) {
            logChatError('Failed to send message', selectedRoomId || undefined, 'sendMessage');
        } finally {
            setIsSending(false);
        }
    }, [editingMessage, isEditingNow, editingMentions, privateReplyTarget, replyToMessage, newMessage, codeBlockState, currentMentions, currentRoomMentions, selectedFiles, effectiveHasRecording, effectiveAudioBlob, isSending, isRoomEncrypted, selectedRoomId, onSendMessage, clearAllFiles, cancelRecording, maxAttachments, showAttachmentLimitNotice]);

    const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (showMentionSuggestions && (e.key === 'Tab' || e.key === 'Enter')) {
            e.preventDefault();
            return;
        }

        const withModifier = e.metaKey || e.ctrlKey;
        if (withModifier) {
            const key = e.key.toLowerCase();
            if (key === 'b' && !e.shiftKey) {
                e.preventDefault();
                applyFormatting(BOLD_MARKER);
                return;
            }
            if (key === 'i' && !e.shiftKey) {
                e.preventDefault();
                applyFormatting(ITALIC_MARKER);
                return;
            }
            if (key === 'e' && !e.shiftKey) {
                e.preventDefault();
                applyFormatting(CODE_MARKER);
                return;
            }
            if (key === 'x' && e.shiftKey) {
                e.preventDefault();
                applyFormatting(STRIKETHROUGH_MARKER);
                return;
            }
        }

        onTyping();

        if (e.key === 'Enter' && codeBlockState) {
            if (e.nativeEvent.isComposing) {
                return;
            }
            e.preventDefault();
            const offsets = mentionInputRef.current?.getSelectionOffsets() ?? {
                start: newMessage.length,
                end: newMessage.length,
            };
            const nextText = newMessage.slice(0, offsets.start) + '\n' + newMessage.slice(offsets.end);
            handleComposerTextChange(nextText, true);
            return;
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            const offsets = mentionInputRef.current?.getSelectionOffsets() ?? {
                start: newMessage.length,
                end: newMessage.length,
            };
            const nextText = newMessage.slice(0, offsets.start) + '\n' + newMessage.slice(offsets.end);
            if (reduceCodeBlockChange(newMessage, null, nextText).block) {
                handleComposerTextChange(nextText, true);
                return;
            }
            handleSend();
        }
    }, [showMentionSuggestions, onTyping, handleSend, applyFormatting, codeBlockState, newMessage, handleComposerTextChange]);

    const handleMentionTrigger = useCallback((mentionMatch: RegExpMatchArray | null) => {
        if (!mentionMatch) {
            setShowMentionSuggestions(false);
            setFilteredUsers([]);
        } else {
            const filtered = usersWithStatus.filter((user) => {
                const displayName = 'displayName' in user
                    ? user.displayName
                    : `${user.firstName} ${user.lastName}`.trim();
                return matchesMentionQuery(displayName, mentionMatch[1]);
            });

            setFilteredUsers(filtered);
            setShowMentionSuggestions(!!filtered.length);
        }
    }, [usersWithStatus]);

    const handleRoomMentionTrigger = useCallback((roomMatch: RegExpMatchArray | null) => {
        if (!roomMatch) {
            setShowRoomSuggestions(false);
            setFilteredRooms([]);
            return;
        }

        const query = roomMatch[1].toLowerCase().trim();
        const matches = (availableRooms || [])
            .filter(room => room.roomId !== selectedRoomId && !!room.name)
            .filter(room => !query || room.name.toLowerCase().includes(query))
            .slice(0, 5);

        setFilteredRooms(matches);
        // "#" turns up in ordinary writing, so an empty list is the signal that
        // this one was not meant as a room link — same guard mobile uses.
        setShowRoomSuggestions(matches.length > 0);
    }, [availableRooms, selectedRoomId]);

    const handleRoomMentionSelect = useCallback((room: RoomMentionTarget) => {
        mentionInputRef.current?.insertRoomMention(room);
        setShowRoomSuggestions(false);
        setFilteredRooms([]);
    }, []);

    const handleMentionSelect = useCallback((user: any) => {
        if (mentionInputRef.current) {
            const normalizedUser = {
                userId: user.userId,
                displayName: user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
            };
            mentionInputRef.current.insertMention(normalizedUser);

            setTimeout(() => {
                setShowMentionSuggestions(false);
            });
        }
    }, []);

    const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        document.getElementById('mention-text-input')?.focus();
        const files = Array.from(event.target.files || []);

        addFiles(files);

        if (event.target) {
            event.target.value = '';
        }
    }, [addFiles]);

    const handlePastedFiles = useCallback((files: File[]) => {
        addFiles(files);
    }, [addFiles]);

    const openFilePicker = useCallback((input: React.RefObject<HTMLInputElement>) => {
        if (isAttachmentLimitReached) {
            showAttachmentLimitNotice(0);
            return;
        }

        input.current?.click();
    }, [isAttachmentLimitReached, showAttachmentLimitNotice]);

    const removeFile = useCallback((index: number) => {
        const preview = filePreviews[index];
        if (preview.preview) {
            URL.revokeObjectURL(preview.preview);
        }

        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
        setFilePreviews(prev => prev.filter((_, i) => i !== index));
    }, [filePreviews]);

    const handleCancelRecording = useCallback(() => {
        cancelRecording();
        if (currentRoomAudioURL) {
            URL.revokeObjectURL(currentRoomAudioURL);
        }
        setCurrentRoomAudioURL(null);
        setCurrentRoomAudioBlob(null);
        setCurrentRoomAudioLevels([]);

        // Clear audio from the current room's draft
        const currentRoomKey = selectedRoomId || 'new-chat';
        const currentDraft = roomDraftsRef.current.get(currentRoomKey);
        if (currentDraft) {
            roomDraftsRef.current.set(currentRoomKey, {
                ...currentDraft,
                audioURL: null,
                audioBlob: null,
                audioLevels: [],
            });
        }
    }, [cancelRecording, currentRoomAudioURL, selectedRoomId]);

    const handleVoiceRecordingClick = useCallback(() => {
        if (isRecording) {
            stopRecording();
        } else if (effectiveHasRecording) {
            handleCancelRecording();
        } else {
            if (isAttachmentLimitReached) {
                showAttachmentLimitNotice(0);
                return;
            }

            startRecording();
        }
    }, [isRecording, effectiveHasRecording, startRecording, stopRecording, handleCancelRecording, isAttachmentLimitReached, showAttachmentLimitNotice]);

    const handleEmojiPick = useCallback((emoji: { emoji: string }) => {
        const inputElement = document.getElementById('mention-text-input');
        if (inputElement) {
            inputElement.focus();

            const selection = window.getSelection();
            const currentRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

            // Opening the picker moves focus out of the composer, so the live
            // selection may still point at whatever the user last clicked. Only
            // insert at the caret when that caret is actually in the composer;
            // otherwise fall back to the end of the message.
            const isCaretInComposer = Boolean(
                currentRange && inputElement.contains(currentRange.commonAncestorContainer)
            );

            if (selection) {
                let targetRange = currentRange;

                if (!isCaretInComposer) {
                    targetRange = document.createRange();
                    targetRange.selectNodeContents(inputElement);
                    targetRange.collapse(false);
                }

                const emojiNode = document.createTextNode(emoji.emoji);
                targetRange!.deleteContents();
                targetRange!.insertNode(emojiNode);

                const newRange = document.createRange();
                newRange.setStartAfter(emojiNode);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);

                const inputEvent = new Event('input', { bubbles: true });
                inputElement.dispatchEvent(inputEvent);
            } else {
                document.execCommand('insertText', false, emoji.emoji);
            }
        }
        setShowEmojiPicker(false);
    }, []);

    React.useEffect(() => {
        if (!showMentionSuggestions || filteredUsers.length === 0) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setSelectedMentionIndex(prev =>
                        prev < filteredUsers.length - 1 ? prev + 1 : 0
                    );
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setSelectedMentionIndex(prev =>
                        prev > 0 ? prev - 1 : filteredUsers.length - 1
                    );
                    break;
                case 'Tab':
                case 'Enter':
                    e.preventDefault();
                    e.stopPropagation();
                    if (filteredUsers[selectedMentionIndex]) {
                        handleMentionSelect(filteredUsers[selectedMentionIndex]);
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    setShowMentionSuggestions(false);
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showMentionSuggestions, filteredUsers, selectedMentionIndex, handleMentionSelect]);

    React.useEffect(() => {
        setSelectedMentionIndex(0);
    }, [filteredUsers]);

    React.useEffect(() => {
        if (!showMentionSuggestions) return;

        const selectedItem = mentionListRef.current?.querySelector<HTMLElement>('[data-selected="true"]');
        selectedItem?.scrollIntoView({block: 'nearest'});
    }, [showMentionSuggestions, selectedMentionIndex]);

    React.useEffect(() => {
        if (!isRecording && audioURL && audioBlob) {
            setCurrentRoomAudioURL(audioURL);
            setCurrentRoomAudioBlob(audioBlob);
            setCurrentRoomAudioLevels(audioLevels);
        }
    }, [isRecording, audioURL, audioBlob, audioLevels]);

    React.useEffect(() => {
        const currentRoomKey = selectedRoomId || 'new-chat';
        const previousRoomKey = previousRoomIdRef.current;
        setAttachmentLimitNotice(null);
        pasteTableUndoRef.current = null;
        setPasteTableUndo(null);
        setTooLargeError(null);

        const parkedEditDraft = draftBeforeEditRef.current;
        const wasEditingWhenRoomChanged = parkedEditDraft !== null;
        const draftForPreviousRoom: RoomDraft = parkedEditDraft ?? {
            message: parkedComposerText(newMessage, codeBlockState),
            mentions: currentMentions,
            roomMentions: currentRoomMentions,
            files: selectedFiles,
            filePreviews,
            audioURL: currentRoomAudioURL,
            audioBlob: currentRoomAudioBlob,
            audioLevels: currentRoomAudioLevels,
        };

        if (wasEditingWhenRoomChanged) {
            draftBeforeEditRef.current = null;
            setEditingUiSuppressed(true);
        }

        if (previousRoomKey && (draftForPreviousRoom.message.trim() || draftForPreviousRoom.files.length > 0 || draftForPreviousRoom.audioBlob)) {
            const oldDraft = roomDraftsRef.current.get(previousRoomKey);
            if (oldDraft?.audioURL) {
                URL.revokeObjectURL(oldDraft.audioURL);
            }
            if (oldDraft?.filePreviews) {
                oldDraft.filePreviews.forEach(item => {
                    if (item.preview) {
                        URL.revokeObjectURL(item.preview);
                    }
                });
            }

            roomDraftsRef.current.set(previousRoomKey, draftForPreviousRoom);
        }

        if (recordingControlsRef.current.isRecording) {
            recordingControlsRef.current.cancelRecording();
        }

        if (currentRoomAudioURL) {
            URL.revokeObjectURL(currentRoomAudioURL);
        }
        filePreviews.forEach(item => {
            if (item.preview) {
                URL.revokeObjectURL(item.preview);
            }
        });

        codeBlockStateRef.current = null;
        setCodeBlockState(null);
        setFormatSelection(null);

        const draft = roomDraftsRef.current.get(currentRoomKey);
        if (draft) {
            previousComposerTextRef.current = draft.message;
            setNewMessage(draft.message);
            setCurrentMentions(draft.mentions);
            setCurrentRoomMentions(draft.roomMentions || []);
            setSelectedFiles(draft.files);

            const regeneratedPreviews = draft.filePreviews.map(item => {
                if (item.file.type.startsWith('image/')) {
                    return {
                        file: item.file,
                        preview: URL.createObjectURL(item.file),
                        type: item.type
                    };
                }
                return item;
            });
            setFilePreviews(regeneratedPreviews);

            if (draft.audioBlob) {
                const newAudioURL = URL.createObjectURL(draft.audioBlob);
                setCurrentRoomAudioURL(newAudioURL);
                setCurrentRoomAudioBlob(draft.audioBlob);
                setCurrentRoomAudioLevels(draft.audioLevels);
            } else {
                setCurrentRoomAudioURL(null);
                setCurrentRoomAudioBlob(null);
                setCurrentRoomAudioLevels([]);
            }
        } else {
            previousComposerTextRef.current = '';
            setNewMessage('');
            setCurrentMentions([]);
            setCurrentRoomMentions([]);
            clearAllFiles();
            setCurrentRoomAudioURL(null);
            setCurrentRoomAudioBlob(null);
            setCurrentRoomAudioLevels([]);
            if (hasRecording || effectiveHasRecording) {
                recordingControlsRef.current.cancelRecording();
            }
        }

        previousRoomIdRef.current = currentRoomKey;
    }, [selectedRoomId]);

    React.useEffect(() => {
        if (!inputRef.current) return;

        let lastHeight = 0;
        let timeoutId: number | null = null;

        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const newHeight = entry.contentRect.height;

                if (Math.abs(newHeight - lastHeight) > 1) {
                    lastHeight = newHeight;

                    if (timeoutId) clearTimeout(timeoutId);
                    timeoutId = setTimeout(() => {
                        onHeightChange(newHeight);
                    }, 50);
                }
            }
        });

        resizeObserver.observe(inputRef.current);

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            resizeObserver.disconnect();
        };
    }, [onHeightChange]);

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (showEmojiPicker) {
                const target = event.target as Element;
                if (!target.closest('.emoji-picker-container') && !target.closest('.emoji-button')) {
                    setShowEmojiPicker(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmojiPicker]);

    const effectiveMessageForSend = codeBlockState ? closeCodeBlock(newMessage, codeBlockState).text : newMessage;
    const canSendCurrentText = hasSendableContent(effectiveMessageForSend, currentMentions, currentRoomMentions);

    return (
        <div ref={inputRef} className="relative">
            {diffrentTimeZone && timezoneMessage}

            {isEditingNow && (
                <div className="px-4 py-[14px] text-tiny bg-canvas border border-fill-subtle rounded-t-2xl max-w-[848px] mx-auto">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Pencil size={14} className="text-ink-muted"/>
                            <span className="text-ink-secondary font-medium">{intl.formatMessage({id: 'chat.edit.banner', defaultMessage: 'Editing message'})}</span>
                        </div>
                        <button
                            onClick={onEditCancel}
                            aria-label={intl.formatMessage({id: 'chat.edit.cancel', defaultMessage: 'Cancel edit'})}
                            className="text-blue-600 hover:text-blue-800"
                        >
                            <X className="p-1.5"/>
                        </button>
                    </div>
                </div>
            )}

            {replyToMessage && !isEditingNow && (
                <div className="px-4 py-[14px] text-tiny bg-canvas border border-fill-subtle rounded-t-2xl max-w-[848px] mx-auto">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <div className="flex flex-col">
                                {getReplyMessagePreview()}
                            </div>
                        </div>
                        <button
                            data-testid="chat.composer.reply-cancel-btn"
                            onClick={() => {
                                setTooLargeError(null);
                                onReplyCancel();
                            }}
                            className="text-blue-600 hover:text-blue-800"
                        >
                            <X className="p-1.5"/>
                        </button>
                    </div>
                </div>
            )}

            {privateReplyTarget && !isEditingNow && (
                <div className="px-4 py-[14px] text-tiny bg-canvas border border-fill-subtle rounded-t-2xl max-w-[848px] mx-auto">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <span className="text-ink-secondary font-medium">
                                {intl.formatMessage({
                                    id: 'chat.private-reply.banner',
                                    defaultMessage: 'Replying privately to {name}'
                                }, {name: privateReplyTarget.senderName})}
                            </span>
                        </div>
                        <button
                            data-testid="chat.composer.private-reply-cancel-btn"
                            onClick={() => {
                                setTooLargeError(null);
                                onPrivateReplyCancel();
                            }}
                            aria-label={intl.formatMessage({id: 'chat.private-reply.cancel', defaultMessage: 'Cancel private reply'})}
                            className="text-blue-600 hover:text-blue-800"
                        >
                            <X className="p-1.5"/>
                        </button>
                    </div>
                </div>
            )}

            <div>
                <div className="flex items-center gap-4 relative justify-center">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="*/*"
                        multiple={availableAttachmentSlots > 1}
                        onChange={handleFileSelect}
                        className="hidden"
                    />
                    <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/*"
                        multiple={availableAttachmentSlots > 1}
                        onChange={handleFileSelect}
                        className="hidden"
                    />
                    <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/*"
                        multiple={availableAttachmentSlots > 1}
                        onChange={handleFileSelect}
                        className="hidden"
                    />
                    <input
                        ref={documentInputRef}
                        type="file"
                        accept="*/*"
                        multiple={availableAttachmentSlots > 1}
                        onChange={handleFileSelect}
                        className="hidden"
                    />

                    <div className="flex-1 max-w-[848px] mx-auto">
                        <div className={`flex flex-col w-full border border-fill-subtle shadow-md bg-surface focus-within:border-blue-500 p-2 ${(replyToMessage || privateReplyTarget || isEditingNow) ? 'rounded-b-2xl' : 'rounded-2xl'}`}>
                            {attachmentLimitNotice && (
                                <div
                                    role="alert"
                                    className="mb-3 rounded-lg border border-danger-tint  bg-danger-tint px-3 py-2 text-sm text-ink-secondary"
                                >
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500"/>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium text-ink">Attachment limit reached</div>
                                            <div className="mt-0.5 text-xs text-ink-muted-strong">
                                                {attachmentLimitNotice.rejectedCount > 0 ? (
                                                    <>
                                                        {attachmentLimitNotice.acceptedCount > 0 && (
                                                            <>
                                                                {attachmentLimitNotice.acceptedCount} file{attachmentLimitNotice.acceptedCount !== 1 ? 's' : ''} added.{' '}
                                                            </>
                                                        )}
                                                        {attachmentLimitNotice.rejectedCount} file{attachmentLimitNotice.rejectedCount !== 1 ? 's were' : ' was'} not added because the limit is {attachmentLimitNotice.maxAttachments}.
                                                    </>
                                                ) : (
                                                    <>Remove an attachment to add another file. The limit is {attachmentLimitNotice.maxAttachments}.</>
                                                )}
                                            </div>
                                            {attachmentLimitNotice.rejectedFileNames.length > 0 && (
                                                <div className="mt-2 flex max-h-24 flex-wrap gap-1 overflow-y-auto pr-1">
                                                    {attachmentLimitNotice.rejectedFileNames.map((fileName, index) => (
                                                        <span
                                                            key={`${fileName}-${index}`}
                                                            className="max-w-full truncate rounded border border-line bg-surface px-1.5 py-0.5 text-xs text-ink-muted-strong"
                                                            title={fileName}
                                                        >
                                                            {fileName}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setAttachmentLimitNotice(null)}
                                            data-tooltip="Dismiss"
                                        >
                                            <X className="p-1.5" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {tooLargeError && (
                                <div
                                    role="alert"
                                    data-testid="chat.composer.too-long"
                                    className="mb-3 rounded-lg border border-danger-tint bg-danger-tint px-3 py-2 text-sm text-ink-secondary"
                                >
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500"/>
                                        <div className="min-w-0 flex-1">
                                            {intl.formatMessage({
                                                id: 'chat.composer.too-long.error',
                                                defaultMessage: 'Message is too long to send ({actualKB} KB, limit ~{limitKB} KB). Shorten it or send it as a file.'
                                            }, {
                                                actualKB: bytesToKB(tooLargeError.estimatedBytes),
                                                limitKB: bytesToKB(MATRIX_MAX_EVENT_BYTES),
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {effectiveHasRecording && (
                                <VoiceRecordingPreview
                                    isRecording={isRecording}
                                    recordingLength={recordingLength}
                                    audioURL={effectiveAudioURL}
                                    onCancel={handleCancelRecording}
                                    onStop={stopRecording}
                                    isSending={isSending}
                                    audioLevels={effectiveAudioLevels}
                                    maxWidth={inputWidth}
                                />
                            )}

                            {selectedFiles.length > 0 && (
                                <div className="mb-3 p-2 bg-canvas rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm text-ink-muted-strong font-medium">
                                            {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
                                        </span>
                                        <button
                                            onClick={clearAllFiles}
                                            className="text-xs text-red-500 hover:text-red-700 transition-colors"
                                            disabled={isSending}
                                        >
                                            Clear all
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap">
                                        {filePreviews.map((item, index) => (
                                            <FilePreview
                                                key={index}
                                                file={item.file}
                                                preview={item.preview}
                                                index={index}
                                                onRemove={removeFile}
                                                isSending={isSending}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {!codeBlockState && pasteTableUndo && (
                                <div data-testid="chat.composer.paste-table-chip" className="flex items-center justify-between mb-2 px-2 py-1 rounded bg-canvas border border-line text-xs text-ink-muted-strong">
                                    <span>{intl.formatMessage({id: 'chat.composer.paste-table.message', defaultMessage: 'Pasted as table'})}</span>
                                    <button
                                        type="button"
                                        data-testid="chat.composer.paste-table-undo-btn"
                                        onClick={handleUndoPasteTable}
                                        className="text-blue-600 hover:text-blue-800"
                                    >
                                        {intl.formatMessage({id: 'chat.composer.paste-table.undo', defaultMessage: 'Undo'})}
                                    </button>
                                </div>
                            )}

                            {codeBlockState && (
                                <div data-testid="chat.composer.code-frame" className="flex items-center justify-between mb-2 px-2 py-1 rounded bg-canvas border border-line text-xs text-ink-muted-strong">
                                    <span>{intl.formatMessage({id: 'chat.composer.code-block', defaultMessage: 'Code block'})}</span>
                                    <button
                                        type="button"
                                        data-testid="chat.composer.code-exit-btn"
                                        onClick={handleExitCodeBlock}
                                        className="text-blue-600 hover:text-blue-800"
                                    >
                                        {intl.formatMessage({id: 'chat.composer.code-block.exit', defaultMessage: 'Exit'})}
                                    </button>
                                </div>
                            )}

                            {!codeBlockState && formatSelection && formatSelection.start !== formatSelection.end && (
                                <ComposerFormattingToolbar onFormat={applyFormatting} disabled={disabled || isSending}/>
                            )}

                            <div className="flex items-center">
                                <MentionTextInput
                                    ref={mentionInputRef}
                                    value={newMessage}
                                    onChange={(e) => handleComposerTextChange(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    onMentionTrigger={handleMentionTrigger}
                                    onMentionsChange={setCurrentMentions}
                                    onRoomMentionTrigger={handleRoomMentionTrigger}
                                    onRoomMentionsChange={setCurrentRoomMentions}
                                    onPasteFiles={handlePastedFiles}
                                    onSelectionChange={setFormatSelection}
                                    onPlainTableInserted={handlePlainTableInserted}
                                    codeBlockOpen={!!codeBlockState}
                                    roomUsers={roomUsers.length ? roomUsers : usersToCreateChat.map(u => ({
                                        userId: u.matrixId,
                                        displayName: u.firstName + ' ' + u.lastName,
                                        avatarUrl: u.avatarUrl
                                    }))}
                                    hydrateMentions={currentMentions}
                                    hydrateRoomMentions={currentRoomMentions}
                                    placeholder={isEditingNow ? intl.formatMessage({id: 'chat.edit.placeholder', defaultMessage: 'Edit message...'}) : replyToMessage ? "Type your reply..." : `Message ${currentRoomName || roomUsers?.map(u => u.displayName).join(', ')}`}
                                    disabled={disabled || isSending}
                                />
                            </div>
                            {isRecording && <div className="w-full">
                                <VoiceRecordingPreview
                                    isRecording={isRecording}
                                    recordingLength={recordingLength}
                                    audioURL={effectiveAudioURL}
                                    onCancel={handleCancelRecording}
                                    onStop={stopRecording}
                                    isSending={isSending}
                                    audioLevels={effectiveAudioLevels}
                                    maxWidth={inputWidth}
                                />
                            </div>}
                            {!isRecording && <div className="flex items-end justify-between">
                                <div className="flex items-start">
                                    <PhoneHQDropdown
                                        showCloseIcon={true}
                                        align="center"
                                        trigger={
                                            <button
                                                data-tooltip={isAttachmentLimitReached ? "Attachment limit reached" : "Upload files"}
                                                disabled={isSending || isEditingNow}
                                            >
                                                <Plus className="button p-1.5" size={28}/>
                                            </button>
                                        }
                                    >
                                        <PhoneHQDropdown.Item onClick={() => openFilePicker(imageInputRef)}>
                                            <Image className="mr-3 h-4 w-4"/>
                                            <div className="w-36">Photos</div>
                                        </PhoneHQDropdown.Item>

                                        <PhoneHQDropdown.Item onClick={() => openFilePicker(videoInputRef)}>
                                            <Film className="mr-3 h-4 w-4"/>
                                            <div className="w-36">Videos</div>
                                        </PhoneHQDropdown.Item>

                                        <PhoneHQDropdown.Item onClick={() => openFilePicker(documentInputRef)}>
                                            <FileIcon className="mr-3 h-4 w-4"/>
                                            <div className="w-36">Files</div>
                                        </PhoneHQDropdown.Item>
                                    </PhoneHQDropdown>

                                    <button
                                        onClick={handleVoiceRecordingClick}
                                        data-tooltip={isRecording ? "Stop recording" : effectiveHasRecording ? "Delete recording" : "Start recording"}
                                        disabled={isSending || isEditingNow}
                                        className={isRecording ? "text-red-500 animate-pulse" : ""}
                                    >
                                        <Mic className="button p-1.5" size={28}/>
                                    </button>
                                    <div className="relative">
                                        <button
                                            className="emoji-button"
                                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                            data-tooltip="Add emoji"
                                            disabled={isSending}
                                        >
                                            <Smile className="button p-1.5" size={28}/>
                                        </button>
                                        <div
                                            className={`absolute bottom-full left-0 mb-2 z-50 emoji-picker-container ${showEmojiPicker ? 'block' : 'hidden'}`}>
                                            <EmojiPicker
                                                onEmojiClick={handleEmojiPick}
                                                searchDisabled={false}
                                                emojiStyle={EmojiStyle['TWITTER']}
                                                skinTonesDisabled={true}
                                                width={300}
                                                height={400}
                                            />
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => {
                                            document.getElementById('mention-text-input')?.focus();
                                            setShowMentionSuggestions(true);
                                            setFilteredUsers(usersWithStatus);
                                        }}
                                        data-tooltip={"Mentions"}
                                        disabled={isSending}
                                    >
                                        <AtSign className="button p-1.5" size={28}/>
                                    </button>
                                </div>

                                <button
                                    aria-label={intl.formatMessage({id: 'chat.composer.send', defaultMessage: 'Send'})}
                                    data-testid="chat.composer.send-btn"
                                    onClick={handleSend}
                                    disabled={(!canSendCurrentText && selectedFiles.length === 0 && !effectiveHasRecording) || isSending}
                                    className={isSending ? 'animate-pulse' : ''}
                                >
                                    {isSending ? (
                                        <PhoneHQSpinner className={'animate-spin'}/>
                                    ) : (
                                        <SendHorizontal className="button p-1.5" size={28}/>
                                    )}
                                </button>

                            </div>}
                        </div>
                    </div>
                </div>
            </div>

            <div ref={buttonRef} style={{ bottom: `${inputHeight}px` }} className={`mx-auto max-w-[848px] relative`}>
                <PhoneHQPopover
                    side="top"
                    sideOffset={-5}
                    open={showRoomSuggestions}
                    onOpenChange={setShowRoomSuggestions}
                    anchorRef={buttonRef}
                    modal={false}
                    preventAutoFocus={true}
                >
                    <div className="max-h-72 overflow-y-auto">
                        {filteredRooms.map((room) => (
                            <PhoneHQPopover.Item
                                key={room.roomId}
                                onClick={() => handleRoomMentionSelect(room)}
                            >
                                <div className="flex items-center w-[400px] gap-2">
                                    <Hash size={16} className="text-ink-muted shrink-0"/>
                                    <span className="truncate">{room.name}</span>
                                </div>
                            </PhoneHQPopover.Item>
                        ))}
                    </div>
                </PhoneHQPopover>

                <PhoneHQPopover
                    side="top"
                    sideOffset={-5}
                    open={showMentionSuggestions}
                    onOpenChange={setShowMentionSuggestions}
                    anchorRef={buttonRef}
                    modal={false}
                    preventAutoFocus={true}
                >
                    <div ref={mentionListRef} className="max-h-72 overflow-y-auto">
                        {filteredUsers.map((user: any, index: number) => {
                            const statusInfo = getStatusDisplayInfo(user);
                            const displayName = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim();

                            return (
                                <PhoneHQPopover.Item
                                    key={user.userId}
                                    onClick={() => handleMentionSelect(user)}
                                    className={index === selectedMentionIndex ? 'bg-zinc-800' : ''}
                                    data-selected={index === selectedMentionIndex}
                                >
                                    <div className="flex items-center w-[400px]">
                                        <div className="flex items-center space-x-3 gap-2">
                                            <PhoneHQAvatar name={displayName} src={user.avatarUrl} size={'20px'}/>
                                            <span>{displayName}</span>
                                        </div>
                                        {statusInfo.hasStatus && (
                                            <div className="flex items-center gap-2 text-xs text-ink-muted truncate max-w-[190px]">
                                                {statusInfo.statusIcon && (
                                                    <div className="!w-7 ml-2">
                                                        {hexToEmoji(statusInfo.statusIcon, '!w-4 !h-4')}
                                                    </div>
                                                )}
                                                <span className="max-w-[140px] truncate">{statusInfo.statusName}</span>
                                            </div>
                                        )}
                                    </div>
                                </PhoneHQPopover.Item>
                            );
                        })}
                    </div>
                </PhoneHQPopover>
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.selectedRoomId === nextProps.selectedRoomId &&
        prevProps.roomUsers === nextProps.roomUsers &&
        prevProps.usersToCreateChat === nextProps.usersToCreateChat &&
        prevProps.currentRoomName === nextProps.currentRoomName &&
        prevProps.replyToMessage === nextProps.replyToMessage &&
        prevProps.onReplyCancel === nextProps.onReplyCancel &&
        prevProps.privateReplyTarget === nextProps.privateReplyTarget &&
        prevProps.onPrivateReplyCancel === nextProps.onPrivateReplyCancel &&
        prevProps.editingMessage === nextProps.editingMessage &&
        prevProps.editingBody === nextProps.editingBody &&
        prevProps.editingMentions === nextProps.editingMentions &&
        prevProps.editingRoomMentions === nextProps.editingRoomMentions &&
        prevProps.onEditCancel === nextProps.onEditCancel &&
        prevProps.onSendMessage === nextProps.onSendMessage &&
        prevProps.isRoomEncrypted === nextProps.isRoomEncrypted &&
        prevProps.onTyping === nextProps.onTyping &&

        prevProps.getReplyMessagePreview === nextProps.getReplyMessagePreview &&
        prevProps.disabled === nextProps.disabled &&
        prevProps.onHeightChange === nextProps.onHeightChange &&
        prevProps.diffrentTimeZone === nextProps.diffrentTimeZone &&
        prevProps.onDismissTimezone === nextProps.onDismissTimezone &&
        prevProps.timezoneMessage === nextProps.timezoneMessage
    );
});

ChatMessageInput.displayName = 'ChatMessageInput';

export default ChatMessageInput;
